// Card de uma ideia: tipo, porquê agora, como executar, rascunho sugerido, "criar rascunho" e "não serve".
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarHeart, Megaphone, RefreshCw, Tag, ThumbsDown, Users, Wand2 } from 'lucide-react'
import type { IdeaDto, IdeaType } from '@impulso/shared/api/ai'
import { errorMessage } from '../../lib/api'
import { useToast } from '../../ui/toast'
import { useIdeaDraft, useIdeaFeedback } from './api'

const TYPES: Record<IdeaType, { label: string; cls: string; Icon: typeof Tag }> = {
  offer: { label: 'Oferta', cls: 'green', Icon: Tag },
  seasonal: { label: 'Data especial', cls: 'amber', Icon: CalendarHeart },
  creative: { label: 'Novo criativo', cls: 'purple', Icon: Megaphone },
  audience: { label: 'Público', cls: 'blue', Icon: Users },
}

const REASONS = ['Não trabalho com descontos', 'Não combina com meu negócio', 'Já fiz isso', 'Agora não é o momento']

export default function IdeaCard({ companyId, idea, canEdit }: { companyId: string; idea: IdeaDto; canEdit: boolean }) {
  const t = TYPES[idea.type]
  const navigate = useNavigate()
  const toast = useToast()
  const draft = useIdeaDraft(companyId)
  const feedback = useIdeaFeedback(companyId)
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')

  const createDraft = () =>
    draft.mutate(idea.id, {
      onSuccess: (r) => {
        toast('Rascunho criado. Revise e publique quando quiser.')
        navigate(r.link)
      },
    })

  const sendFeedback = (text: string) => {
    if (text.trim().length < 2) return
    feedback.mutate({ id: idea.id, reason: text.trim() }, { onSuccess: () => toast('Obrigado! Vamos levar isso em conta nas próximas ideias.') })
  }

  return (
    <article className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ gap: 10 }}>
        <span className={`ic ${t.cls}`}><t.Icon size={18} /></span>
        <div style={{ minWidth: 0 }}>
          <span className={`badge ${t.cls}`}>{t.label}</span>
          <h3 style={{ marginTop: 4, overflowWrap: 'anywhere' }}>{idea.title}</h3>
        </div>
        {idea.status === 'drafted' && <span className="badge green" style={{ marginLeft: 'auto' }}>Rascunho criado</span>}
      </div>
      <div>
        <div className="small strong muted">POR QUE AGORA</div>
        <p style={{ overflowWrap: 'anywhere' }}>{idea.whyNow}</p>
      </div>
      <div>
        <div className="small strong muted">COMO FAZER</div>
        <p style={{ overflowWrap: 'anywhere' }}>{idea.howTo}</p>
      </div>
      {idea.draft && (
        <div className="callout" style={{ flexDirection: 'column', gap: 2 }}>
          <span className="small strong">Sugestão de anúncio</span>
          <span className="strong" style={{ color: 'var(--text)', overflowWrap: 'anywhere' }}>{idea.draft.headline}</span>
          <span className="small" style={{ overflowWrap: 'anywhere' }}>{idea.draft.body}</span>
        </div>
      )}

      {(draft.error || feedback.error) && <div className="form-error" role="alert">{errorMessage(draft.error ?? feedback.error)}</div>}

      {canEdit && !asking && (
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn primary" onClick={createDraft} disabled={draft.isPending}>
            {draft.isPending ? <RefreshCw size={16} className="spin" /> : <Wand2 size={16} />} {idea.status === 'drafted' ? 'Criar outro rascunho' : 'Criar rascunho'}
          </button>
          <button className="btn ghost" onClick={() => setAsking(true)}><ThumbsDown size={16} /> Não serve</button>
        </div>
      )}

      {canEdit && asking && (
        <div className="stack" style={{ gap: 8 }}>
          <span className="small strong">Por que não serve? Isso ajuda nas próximas ideias.</span>
          <div className="row wrap" style={{ gap: 6 }}>
            {REASONS.map((r) => (
              <button key={r} type="button" className="chip small" disabled={feedback.isPending} onClick={() => sendFeedback(r)}>{r}</button>
            ))}
          </div>
          <form className="row" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); sendFeedback(reason) }}>
            <input className="input" style={{ flex: 1, minWidth: 0 }} placeholder="Ou escreva o motivo…" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} aria-label="Motivo" />
            <button className="btn" disabled={feedback.isPending || reason.trim().length < 2}>Enviar</button>
          </form>
          <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAsking(false)}>Cancelar</button>
        </div>
      )}
    </article>
  )
}
